import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link, useParams } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { handleUpdate, handleGet } from "../../../controllers/controller.all";
import { AlertCircle } from "lucide-react";
import SubmitButton from "../../../components/ui/SubmitButton";


export default function PeriodUpdate() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    const navigate = useNavigate();
    const { id } = useParams();

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        libelle: "",
        date_deb: "",
        date_fin: "",
        annee: ""
    });
    const [hasAnnee, setHasAnnee] = useState(true);
    const [checking, setChecking] = useState(true);
    const [annees, setAnnees] = useState([]);

    React.useEffect(() => {
        const checkAnneeScolaire = async () => {
            await handleGet(`/ecole-annee/ecole/${ecoleId}`, (res) => {
                if (!res?.annees || res.annees.length === 0) {
                    setHasAnnee(false);
                } else {
                    setAnnees(res.annees);
                }
            }, token);
            setChecking(false);
        };
        if (ecoleId && token) {
            checkAnneeScolaire();
        }
    }, [ecoleId, token]);

    React.useEffect(() => {
        if (id && token) {
            handleGet(`/period/${id}`, (res) => {
                if (res && res.period) {
                    const data = res.period;
                    setFormData({
                        libelle: data.libelle || "",
                        date_deb: data.date_deb || "",
                        date_fin: data.date_fin || "",
                        annee: data.annee || ""
                    });
                }
            }, token);
        }
    }, [id, token]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        const payload = {
            ...formData,
            ecole: ecoleId
        };
        const res = await handleUpdate(e, `/period/${id}`, payload, setLoading, token);
        if (res) {
            navigate("/periods");
        }
    };

    return (
        <div className="flex flex-col gap-6 max-w-3xl mx-auto">
            <div className="flex items-center gap-4">
                <Link to="/periods" className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-500">
                    <ArrowLeft className="h-5 w-5" />
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Modifier la Période</h1>
                    <p className="text-sm text-gray-500">Mettez à jour les informations de cette période.</p>
                </div>
            </div>

            {!checking && !hasAnnee && (
                <div className="bg-orange-50 border border-orange-200 text-orange-800 p-4 rounded-xl flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                    <div>
                        <h3 className="font-semibold">Aucune année scolaire active !</h3>
                        <p className="text-sm mt-1">Vous devez d'abord créer et activer une année scolaire avant de pouvoir ajouter des périodes.</p>
                        <Link to="/annees" className="inline-block mt-3 text-sm font-medium text-orange-800 bg-orange-100 hover:bg-orange-200 px-3 py-1.5 rounded-lg transition">
                            Aller à la gestion des années scolaires
                        </Link>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium text-gray-700">Libellé *</label>
                        <input required type="text" name="libelle" value={formData.libelle} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Ex: Premier Semestre" />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium text-gray-700">Année Scolaire Associée</label>
                        <select name="annee" value={formData.annee} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                            <option value="">Année Active par défaut</option>
                            {annees.map(a => (
                                <option key={a._id} value={a.annee}>{a.annee}</option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Date de début *</label>
                        <input required type="date" name="date_deb" value={formData.date_deb} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Date de fin *</label>
                        <input required type="date" name="date_fin" value={formData.date_fin} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <Link to="/periods" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                        Annuler
                    </Link>
                    <SubmitButton type="submit" isSubmitting={loading} disabled={loading || !hasAnnee} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                        <Save className="h-4 w-4" />
                        {loading ? "Enregistrement..." : "Enregistrer"}
                    </SubmitButton>
                </div>
            </form>
        </div>
    );
}
